import type React from 'react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { canRequestSupport, supportLineOf } from '@onr/schema'
import { addBreadcrumb, captureException, setUser as setSentryUser } from '@sentry/react'
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth'
import Cookies from 'js-cookie'
import { z } from 'zod'

import { AppLoading } from '@/components/organisms/AppLoading/AppLoading.tsx'
import { DashboardSignInFailedPage, type DashboardSignInFailure } from '@/pages/error/DashboardSignInFailedPage.tsx'
import { authTransitions } from '@/sentry/authTransitionRefusal.ts'

import { peekPiloteSession, type PiloteSessionStatus } from './sessionGuard/piloteSessionApi.ts'
import { pinSession, useSessionPhase } from './sessionGuard/sessionStore.ts'
import { isRememberedEmbedSession, rememberEmbedSession } from './embedSession.ts'
import { FirebaseUserContext, type FirebaseUserContextValue } from './FirebaseUserContextValue.ts'
import {
  cameBackFromPiloteSignInRecently,
  forgetPiloteSignInRedirect,
  redirectToPiloteSignIn,
} from './redirectToPiloteSignIn.ts'
import { usePiloteSignIn } from './usePiloteSignIn.ts'

const operationsRoles = new Set(['evening', 'allocation', 'assignment', 'sys_admin', 'escouade', 'central_admin'])

const IdTokenResultCustomClaimsZod = z.object({
  permissions: z.string().toLowerCase().array().default([]),
  sys_admin: z.boolean().optional().default(false),
  super_admin: z.boolean().optional().default(false),
  judicial_admin: z.boolean().optional().default(false),
  regionId: z.string().nullable(),
  piloteUserId: z.number().nullish().default(null),
  firstName: z.string().nullish().default(null),
  lastName: z.string().nullish().default(null),
})

async function buildFirebaseUserContextValue(firebaseUser: User, embedded: boolean) {
  const { claims } = await firebaseUser.getIdTokenResult()
  addBreadcrumb({ category: 'auth', message: 'getIdTokenResult', data: { claims } })
  const idTokenClaims = IdTokenResultCustomClaimsZod.parse(claims)
  const supportRole = supportLineOf(idTokenClaims.permissions)
  const supportRequester = canRequestSupport(idTokenClaims.permissions, idTokenClaims.regionId)
  const supportOnly = !idTokenClaims.permissions.some((permission) => operationsRoles.has(permission))
  if (supportOnly && supportRole === null && !embedded) {
    globalThis.location.href = import.meta.env.VITE_PILOTE_BASE_URL
  }
  const canToggleWaitTime = true
  const canToggleMobileApp = true
  return {
    firebaseUser,
    canToggleWaitTime,
    canToggleMobileApp,
    supportRole,
    supportRequester,
    supportOnly,
    ...idTokenClaims,
  }
}

const getSession = () =>
  import.meta.env.VITE_PILOTE_SESSION ??
  new URLSearchParams(document.location.search).get('session') ??
  Cookies.get('__session')

const peekOrNull = async (session: string): Promise<PiloteSessionStatus | null> => {
  try {
    return await peekPiloteSession(session)
  } catch (error) {
    captureException(error, { level: 'warning' })
    return null
  }
}

const backToPilote = () => {
  globalThis.location.assign(import.meta.env.VITE_PILOTE_BASE_URL)
}

const retrySignIn = () => {
  forgetPiloteSignInRedirect()
  globalThis.location.reload()
}

const useSessionExpiredInEmbed = (embedded: boolean) => {
  const phase = useSessionPhase()
  // The embedded panel has no dialog: once Pilote says the session is over, the panel stops showing
  // data it can no longer act on, whether or not someone has clicked since.
  return embedded && phase !== 'active'
}

export const FirebaseUserProvider: React.FC<{ children: ReactNode; supportEmbedFallback?: ReactNode }> = ({
  children,
  supportEmbedFallback,
}) => {
  const embedded = supportEmbedFallback !== undefined
  const [state, setState] = useState<FirebaseUserContextValue | null>(null)
  const [signInFailure, setSignInFailure] = useState<DashboardSignInFailure | null>(null)
  const sessionExpiredInEmbed = useSessionExpiredInEmbed(embedded)
  const signOut = useCallback(() => {
    setState(null)
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve(getAuth().signOut())
      }, 100)
    })
  }, [setState])

  const failSignIn = useCallback(
    async (session?: string) => {
      if (embedded) {
        setSignInFailure('signInFailed')
        return
      }
      const status = session ? await peekOrNull(session) : { status: 'expired' }
      if (status?.status !== 'expired') {
        setSignInFailure('signInFailed')
        return
      }
      // Redirecting again by itself, moments after coming back from Pilote's sign-in, could loop:
      // the person signs in by hand from this page instead.
      if (cameBackFromPiloteSignInRecently()) {
        setSignInFailure('sessionExpired')
        return
      }
      redirectToPiloteSignIn()
    },
    [embedded]
  )

  const { signIn, isPending } = usePiloteSignIn({
    reportErrorToSentry: false,
    onError: (error) => {
      captureException(error, { level: 'info' })
    },
  })

  const setFirebaseUserContext = useCallback(
    async (firebaseUser: User, session: string) => {
      const value = await buildFirebaseUserContextValue(firebaseUser, embedded)
      pinSession(session, await peekOrNull(session), { embedded })
      forgetPiloteSignInRedirect()
      setSignInFailure(null)
      setState({ ...value, signOut })
      setSentryUser({
        id: firebaseUser.uid,
        email: firebaseUser.email ?? undefined,
        displayName: firebaseUser.displayName,
        permissions: value.permissions,
        sys_admin: value.sys_admin,
        canToggleWaitTime: value.canToggleWaitTime,
        regionId: value.regionId,
      })
      addBreadcrumb({ category: 'auth', message: 'setFirebaseUserContext', data: { value } })
    },
    [signOut, embedded]
  )

  useEffect(() => {
    addBreadcrumb({ category: 'auth', message: 'onAuthStateChanged Registered' })
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    return onAuthStateChanged(getAuth(), async (firebaseUser) => {
      authTransitions.noteAuthUser(firebaseUser?.uid ?? null)
      addBreadcrumb({ category: 'auth', message: 'onAuthStateChanged', data: { firebaseUser } })
      const session = getSession()
      if (!session) {
        await failSignIn()
        return
      }
      if (embedded && firebaseUser && (await isRememberedEmbedSession(session, firebaseUser.uid))) {
        try {
          await setFirebaseUserContext(firebaseUser, session)
          return
        } catch (error) {
          captureException(error, { level: 'info' })
        }
      }
      const userCredential = await signIn({ session: `__session=${session}`, storeToken: !embedded })
      if (!userCredential?.user) {
        await failSignIn(session)
        return
      }
      if (embedded) {
        await rememberEmbedSession(session, userCredential.user.uid)
      }
      try {
        await setFirebaseUserContext(userCredential.user, session)
      } catch (error) {
        captureException(error, { level: 'warning' })
        await failSignIn(session)
      }
    })
  }, [signIn, setFirebaseUserContext, failSignIn, embedded])

  if (signInFailure || sessionExpiredInEmbed) {
    return embedded ? (
      supportEmbedFallback
    ) : (
      <DashboardSignInFailedPage
        reason={signInFailure ?? 'signInFailed'}
        onRetry={retrySignIn}
        onSignIn={redirectToPiloteSignIn}
        onBackToPilote={backToPilote}
      />
    )
  }

  if (isPending || !state) {
    return <AppLoading />
  }

  return <FirebaseUserContext.Provider value={state}>{children}</FirebaseUserContext.Provider>
}
